import { createFileRoute, Link, useRouter } from '@tanstack/react-router';
import { formatInTimeZone } from 'date-fns-tz';
import { ArrowLeft, Edit, Loader2, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useSWRConfig } from 'swr';

import { ActivityTimeline } from '@apps/web/components/admin/activity_timeline';
import { ShiftFormModal } from '@apps/web/components/admin/shift_form_modal';
import { resolveShiftStatus } from '@apps/web/components/dashboard/shift_list_filters';
import { Badge } from '@apps/web/components/ui/badge';
import { Button } from '@apps/web/components/ui/button';
import { Card } from '@apps/web/components/ui/card';
import { getApiClient } from '@apps/web/lib/api_client';
import { toast } from '@apps/web/lib/app_notifications';
import { formatDateAndTimeWithTz } from '@apps/web/lib/date_utils';
import { useAdminShift } from '@apps/web/swr_data_fetching/data_fetching_hooks';
import { type CaregiverRole } from '@libraries/typescript/api_client';

export const Route = createFileRoute('/_app/admin/shifts/$shift-id')({
    head: () => ({
        meta: [
            {
                title: 'Shift Details | Phoebe Admin',
            },
        ],
    }),
    component: ShiftDetailPage,
});

function ShiftDetailPage() {
    const { 'shift-id': shiftId } = Route.useParams();

    return (
        <div className="h-full overflow-x-hidden overflow-y-auto">
            <div className="max-w-5xl p-6">
                <div className="mb-6">
                    <Link
                        to="/admin/shifts"
                        className="text-muted-foreground hover:text-foreground inline-flex items-center gap-2 text-sm"
                    >
                        <ArrowLeft className="h-4 w-4" />
                        Back to Shifts
                    </Link>
                </div>
                <ShiftDetails shiftId={shiftId} />
            </div>
        </div>
    );
}

function ShiftDetails({ shiftId }: { shiftId: string }) {
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const router = useRouter();
    const { mutate } = useSWRConfig();

    const {
        data: shift,
        isLoading,
        mutate: mutateShift,
    } = useAdminShift(shiftId);

    const handleDelete = async () => {
        if (!shiftId || !shift) {
            return;
        }

        const confirmed = window.confirm(
            `Are you sure you want to delete the shift for ${shift.patient_name}? This action cannot be undone.`,
        );

        if (!confirmed) {
            return;
        }

        setIsDeleting(true);

        try {
            await getApiClient().delete({
                url: '/admin/shifts/{shift_id}',
                pathParams: { shift_id: shiftId },
            });

            toast.success('Shift deleted successfully', {
                persistNotification: true,
                notificationType: 'shift_action',
            });
            mutate(
                (key) =>
                    typeof key === 'string' && key.includes('/admin/shifts'),
            );
            router.navigate({ to: '/admin/shifts' });
        } catch (error) {
            toast.error('Failed to delete shift', {
                persistNotification: true,
                notificationType: 'shift_action',
            });
            setIsDeleting(false);
        }
    };

    if (isLoading) {
        return (
            <div className="flex h-full items-center justify-center">
                <div className="text-muted-foreground flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Loading shift details…</span>
                </div>
            </div>
        );
    }

    if (!shift) {
        return (
            <div className="text-muted-foreground flex h-full items-center justify-center">
                Shift not found
            </div>
        );
    }

    const trimmedCarePlan = shift.patient_care_plan?.trim();
    const displayStatus = resolveShiftStatus(shift.status, shift.latest_stage);
    const displayStatusLabel = displayStatus
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (letter) => letter.toUpperCase());

    return (
        <div className="max-w-full min-w-0 space-y-6 overflow-hidden">
            <div className="flex items-center justify-between">
                <h1 className="text-2xl font-semibold">Shift Details</h1>
                <div className="flex items-center gap-2">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setIsEditModalOpen(true)}
                        className="gap-2"
                    >
                        <Edit className="h-4 w-4" />
                        Edit
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={handleDelete}
                        disabled={isDeleting}
                        className="gap-2 text-red-600 hover:text-red-700"
                    >
                        <Trash2 className="h-4 w-4" />
                        {isDeleting ? 'Deleting...' : 'Delete'}
                    </Button>
                    <Badge
                        variant={
                            displayStatus === 'assigned'
                                ? 'default'
                                : 'secondary'
                        }
                    >
                        {displayStatusLabel}
                    </Badge>
                </div>
            </div>
            <Card className="p-6">
                <div className="mb-6">
                    <h3 className="text-muted-foreground text-sm font-medium">
                        Shift ID
                    </h3>
                    <p className="mt-1 font-mono text-sm">{shift.id}</p>
                </div>
                <div className="grid grid-cols-2 gap-6">
                    <div className="space-y-4">
                        <div>
                            <h3 className="text-muted-foreground text-sm font-medium">
                                Patient Name
                            </h3>
                            <p className="mt-1">{shift.patient_name}</p>
                        </div>
                        <div>
                            <h3 className="text-muted-foreground text-sm font-medium">
                                Organization
                            </h3>
                            <p className="mt-1">{shift.organization_name}</p>
                        </div>
                        <div>
                            <h3 className="text-muted-foreground text-sm font-medium">
                                Assigned Caregiver
                            </h3>
                            <p className="mt-1">
                                {shift.assigned_caregiver_name || 'Unassigned'}
                            </p>
                        </div>
                        <div>
                            <h3 className="text-muted-foreground text-sm font-medium">
                                Role Required
                            </h3>
                            <p className="mt-1 capitalize">
                                {shift.role_required || 'Any'}
                            </p>
                        </div>
                    </div>
                    <div className="space-y-4">
                        <div>
                            <h3 className="text-muted-foreground text-sm font-medium">
                                Start Time
                            </h3>
                            <p className="mt-1">
                                {formatDateAndTimeWithTz(
                                    shift.shift_start_time,
                                    shift.organization_timezone,
                                )}
                            </p>
                        </div>
                        <div>
                            <h3 className="text-muted-foreground text-sm font-medium">
                                End Time
                            </h3>
                            <p className="mt-1">
                                {formatDateAndTimeWithTz(
                                    shift.shift_end_time,
                                    shift.organization_timezone,
                                )}
                            </p>
                        </div>
                        <div>
                            <h3 className="text-muted-foreground text-sm font-medium">
                                Pay Rate
                            </h3>
                            <p className="mt-1">
                                {shift.pay_rate
                                    ? `$${(shift.pay_rate / 100).toFixed(2)}/hr`
                                    : 'Not set'}
                            </p>
                        </div>
                        <div>
                            <h3 className="text-muted-foreground text-sm font-medium">
                                Source
                            </h3>
                            <p className="mt-1 capitalize">
                                {shift.source_system || 'System'}
                            </p>
                        </div>
                        <div>
                            <h3 className="text-muted-foreground text-sm font-medium">
                                External ID
                            </h3>
                            <p className="mt-1 font-mono text-sm">
                                {shift.external_id || 'None'}
                            </p>
                        </div>
                    </div>
                </div>
                {shift.patient_address && (
                    <div className="mt-6">
                        <h3 className="text-muted-foreground text-sm font-medium">
                            Patient Address
                        </h3>
                        <p className="mt-1">{shift.patient_address}</p>
                    </div>
                )}
                {shift.skills_required && shift.skills_required.length > 0 && (
                    <div className="mt-6">
                        <h3 className="text-muted-foreground text-sm font-medium">
                            Skills Required
                        </h3>
                        <div className="mt-2 flex flex-wrap gap-2">
                            {shift.skills_required.map((skill) => (
                                <Badge key={skill} variant="outline">
                                    {skill.replace('_', ' ')}
                                </Badge>
                            ))}
                        </div>
                    </div>
                )}
                {shift.notes_for_caregiver && (
                    <div className="mt-6">
                        <h3 className="text-muted-foreground text-sm font-medium">
                            Notes for Caregiver
                        </h3>
                        <p className="mt-1">{shift.notes_for_caregiver}</p>
                    </div>
                )}
                {shift.internal_shift_notes && (
                    <div className="mt-6">
                        <h3 className="text-muted-foreground text-sm font-medium">
                            Internal Notes
                        </h3>
                        <p className="mt-1">{shift.internal_shift_notes}</p>
                    </div>
                )}
            </Card>
            {trimmedCarePlan && (
                <div>
                    <h2 className="text-xl font-semibold">Care Plan</h2>
                    <Card className="mt-3 p-4">
                        <p className="text-muted-foreground text-sm whitespace-pre-wrap">
                            {shift.patient_care_plan}
                        </p>
                    </Card>
                </div>
            )}
            <ActivityTimeline
                entityType="shift"
                entityId={shift.id}
                organizationId={shift.organization_id}
            />
            <ShiftFormModal
                isOpen={isEditModalOpen}
                onClose={() => setIsEditModalOpen(false)}
                mode="edit"
                shiftId={shiftId}
                timezone={shift.organization_timezone}
                onSaveSuccess={() => mutateShift()}
                initialData={{
                    mode: shift.mode,
                    organization_id: shift.organization_id,
                    patient_name: shift.patient_name,
                    patient_address: shift.patient_address || '',
                    shift_start_time: formatInTimeZone(
                        shift.shift_start_time,
                        shift.organization_timezone,
                        "yyyy-MM-dd'T'HH:mm",
                    ),
                    shift_end_time: formatInTimeZone(
                        shift.shift_end_time,
                        shift.organization_timezone,
                        "yyyy-MM-dd'T'HH:mm",
                    ),
                    role_required: shift.role_required as CaregiverRole,
                    pay_rate: shift.pay_rate ? shift.pay_rate / 100 : undefined,
                    notes_for_caregiver: shift.notes_for_caregiver || '',
                    internal_shift_notes: shift.internal_shift_notes || '',
                    status: shift.status,
                    assigned_caregiver_id:
                        shift.assigned_caregiver_id || undefined,
                }}
            />
        </div>
    );
}
